import { useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { X, Download, Zap, Film } from "lucide-react";

const CDN = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";

function fmt(s) {
  const h = Math.floor(s / 3600).toString().padStart(2, "0");
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, "0");
  const sec = (s % 60).toFixed(3).padStart(6, "0");
  return `${h}:${m}:${sec}`;
}

export default function ExportModal({ layout, panelARef, panelBRef, onClose }) {
  const [phase, setPhase] = useState("config"); // config | loading | processing | done | error
  const [quality, setQuality] = useState("fast"); // fast | quality
  const [log, setLog] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [filename, setFilename] = useState("");
  const ffmpegRef = useRef(null);
  const cancelled = useRef(false);

  async function run() {
    const fileA = panelARef.current?.getVideoFile?.();
    const fileB = panelBRef.current?.getVideoFile?.();
    const timeA = panelARef.current?.getCurrentTime?.() ?? 0;
    const timeB = panelBRef.current?.getCurrentTime?.() ?? 0;

    if (!fileA && !fileB) {
      setPhase("error");
      setLog("At least one panel must have a local file loaded. URL and YouTube sources cannot be used for export.");
      return;
    }

    cancelled.current = false;

    try {
      // ── 1. Load FFmpeg WASM ──────────────────────────────────────────────
      setPhase("loading");
      setLog("Loading FFmpeg…");

      const ffmpeg = new FFmpeg();
      ffmpegRef.current = ffmpeg;

      ffmpeg.on("log", ({ message }) => {
        if (cancelled.current) return;
        setLog(message);
      });
      await ffmpeg.load({
        coreURL: await toBlobURL(`${CDN}/ffmpeg-core.js`,   "text/javascript"),
        wasmURL: await toBlobURL(`${CDN}/ffmpeg-core.wasm`, "application/wasm"),
      });

      if (cancelled.current) return;

      // ── 2. Write input files ─────────────────────────────────────────────
      setPhase("processing");
      setLog("Preparing files…");

      const extA = fileA ? fileA.name.split(".").pop() : "mp4";
      const extB = fileB ? fileB.name.split(".").pop() : "mp4";

      if (fileA) await ffmpeg.writeFile(`inputA.${extA}`, await fetchFile(fileA));
      if (fileB) await ffmpeg.writeFile(`inputB.${extB}`, await fetchFile(fileB));

      if (cancelled.current) return;

      // ── 3. Build FFmpeg command ──────────────────────────────────────────
      const inA = fileA ? `inputA.${extA}` : `inputB.${extB}`;
      const inB = fileB ? `inputB.${extB}` : `inputA.${extA}`;
      const ssA = fmt(timeA);
      const ssB = fmt(timeB);

      // Scale + stack: normalize both inputs to same height (hstack) or width (vstack),
      // capped at 1280/720 to avoid huge WASM buffers
      const filterStr = layout === "horizontal"
        ? "[0:v]scale=-2:'min(720,ih)'[a];[1:v]scale=-2:'min(720,ih)'[b];[a][b]hstack=inputs=2[v]"
        : "[0:v]scale='min(1280,iw)':-2[a];[1:v]scale='min(1280,iw)':-2[b];[a][b]vstack=inputs=2[v]";

      const preset = quality === "fast" ? "ultrafast" : "fast";
      const crf    = quality === "fast" ? "28" : "23";
      const outName = `dualview-${layout}-${Date.now()}.mp4`;

      const cmd = [
        "-ss", ssA, "-i", inA,
        "-ss", ssB, "-i", inB,
        "-filter_complex", filterStr,
        "-map", "[v]",
        "-c:v", "libx264",
        "-preset", preset,
        "-crf", crf,
        "-movflags", "+faststart",
        "-y", outName,
      ];

      setLog("Encoding…");
      await ffmpeg.exec(cmd);

      if (cancelled.current) return;

      // ── 4. Read output and create download URL ───────────────────────────
      const data = await ffmpeg.readFile(outName);
      const blob = new Blob([data.buffer], { type: "video/mp4" });
      const url  = URL.createObjectURL(blob);

      setDownloadUrl(url);
      setFilename(outName);
      setPhase("done");
      setLog("Done!");

    } catch (e) {
      if (cancelled.current) return;
      setPhase("error");
      setLog(e?.message ?? String(e));
    }
  }

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 w-full max-w-md text-white">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl uppercase tracking-tight">Export</h2>
          {(phase === "config" || phase === "done" || phase === "error") && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-white/10 text-white/50 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Layout badge */}
        <div className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-white/40">
          <span>Layout:</span>
          <span className="text-[#007AFF] font-bold">{layout}</span>
        </div>

        {/* ── Config ── */}
        {phase === "config" && (
          <div className="space-y-5">
            {/* Quality */}
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-white/50 mb-2">Quality</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setQuality("fast")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                    quality === "fast"
                      ? "border-[#007AFF] bg-[#007AFF]/20 text-[#007AFF]"
                      : "border-white/15 bg-white/[0.03] text-white/50 hover:text-white hover:bg-white/[0.08]"
                  }`}
                >
                  <Zap className="w-3 h-3" /> Fast
                </button>
                <button
                  onClick={() => setQuality("quality")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                    quality === "quality"
                      ? "border-[#007AFF] bg-[#007AFF]/20 text-[#007AFF]"
                      : "border-white/15 bg-white/[0.03] text-white/50 hover:text-white hover:bg-white/[0.08]"
                  }`}
                >
                  <Film className="w-3 h-3" /> Better quality
                </button>
              </div>
              <p className="text-[10px] text-white/30 mt-1.5">
                {quality === "fast" ? "Ultrafast encode — ~10× faster, slightly larger file." : "Slower encode — smaller file, better compression."}
              </p>
            </div>

            <button
              onClick={run}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[#007AFF] hover:bg-[#0066DD] text-sm font-semibold transition-colors"
            >
              Start Export
            </button>
            <p className="text-[10px] text-white/30 text-center">
              Processed entirely in your browser — no data uploaded.
            </p>
          </div>
        )}

        {/* ── Processing ── */}
        {(phase === "loading" || phase === "processing") && (
          <div className="space-y-3">
            <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full w-full relative">
                <div className="absolute inset-0 bg-[#007AFF]/30 rounded-full" />
                <div
                  className="absolute inset-y-0 w-1/3 bg-[#007AFF] rounded-full"
                  style={{ animation: "indeterminate 1.4s ease-in-out infinite" }}
                />
              </div>
            </div>
            <p className="text-xs text-white/50 font-mono truncate">{log}</p>
            <p className="text-[10px] text-white/30 mt-1">
              Processing in browser — no data uploaded.
            </p>
          </div>
        )}

        {/* ── Done ── */}
        {phase === "done" && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
              Video composed successfully.
            </div>
            <button
              onClick={handleDownload}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-[#007AFF] hover:bg-[#0066DD] text-sm font-semibold transition-colors"
            >
              <Download className="w-4 h-4" />
              Download {filename}
            </button>
          </div>
        )}

        {/* ── Error ── */}
        {phase === "error" && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm whitespace-pre-wrap break-words">
            {log}
          </div>
        )}

        {/* Init spinner */}
        {phase === "init" && (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
